import { Accordion, AccordionTab } from '@lmig/lmds-react-accordion';
import { useUserData } from 'Foundation/User/UserContext';
import { useEffect, useState } from 'react';
import { useSessionStorage } from 'Utilities/UseStorage';
import LinkSection from './LinkSection';
import { formatId } from '../../../Utilities/StringFormatter';

interface ISectionLinksProps {
  sections: IReactNavSection[];
  filterTerm?: string;
  usesFavorites: boolean;
  id: string;
  usesContactLinks?: boolean;
}

export function AccordionLinks({ sections, filterTerm, usesFavorites, id, usesContactLinks }: ISectionLinksProps) {
  const { userData } = useUserData();
  const [hiddenAccordions, setHiddenAccordions] = useState<string[]>([]);
  const expirationMinutes: number = 8 * 60;
  const [openAccordions, setOpenAccordions] = useSessionStorage<string[] | undefined>(
    'accordion_states_' + formatId(id),
    undefined,
    expirationMinutes,
  );

  useEffect(() => {
    if (sections?.length === 1 && sections[0].Id && !openAccordions)
      //default accordion to open if theres only one
      setOpenAccordions({ data: [sections[0].Id] });
    if (sections?.some((section) => section.AccordionOpen === true)) {
      const accordionsToOpen = sections
        .filter((section) => section.AccordionOpen === true && section.Id)
        .map((section) => section.Id!);
      setOpenAccordions({ data: accordionsToOpen });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isClosed = (id: string | undefined) => id !== undefined && !(openAccordions ?? [])?.includes(id);
  const isVisible = (id: string | undefined) => id !== undefined && !hiddenAccordions.includes(id);

  const addAccordion = (id: string): string[] => {
    let open: string[] = openAccordions ?? [];
    open.push(id);
    return open;
  };

  const removeAccordion = (id: string): string[] => {
    let open: string[] = openAccordions ?? [];
    return open.filter((x) => x !== id);
  };

  const onAccordionClick = (id: string | undefined) => {
    if (id) {
      if (isClosed(id)) setOpenAccordions({ data: addAccordion(id) });
      else setOpenAccordions({ data: removeAccordion(id) });
    }
  };

  //hide accordion titles when they contain no links during a search
  const showHideAccordion = (index: number, show: boolean) => {
    if (
      filterTerm !== undefined &&
      filterTerm.length > 0 &&
      index !== undefined &&
      isVisible(index.toString()) &&
      !show
    )
      setHiddenAccordions((hiddenAccordions) => [...hiddenAccordions, index.toString()]);
    else if (show && hiddenAccordions.includes(index.toString()))
      setHiddenAccordions((hiddenAccordions) => hiddenAccordions.filter((x) => x !== index.toString()));
  };
  const getHiddenClassNames = (): string => {
    return hiddenAccordions.map((x) => `hide-accordion-${x}`).join(' ');
  };

  return (
    <>
      {userData?.state && sections && (
        <div className="slider-panel-link-box">
          <Accordion as="h4" className={getHiddenClassNames()}>
            {sections!.map(
              (x, i) =>
                x.IncludedStates &&
                x.IncludedStates.includes(userData.state) && (
                  <AccordionTab
                    labelVisual={x.Title}
                    active={!isClosed(x.Id) || (filterTerm !== undefined && filterTerm.length > 0)}
                    onClick={() => onAccordionClick(x.Id)}
                  >
                    <LinkSection
                      key={i}
                      {...x}
                      filterTerm={filterTerm}
                      showHideAccordion={(show: boolean) => showHideAccordion(i, show)}
                      Favoritable={usesFavorites}
                      usesContactLinks={usesContactLinks}
                    />
                  </AccordionTab>
                ),
            )}
          </Accordion>
        </div>
      )}
      {hiddenAccordions?.length === sections?.length && filterTerm !== undefined && filterTerm.length > 0 && (
        <div className="empty-nav-section">
          <span>There are no results matching this entry.</span>
        </div>
      )}
    </>
  );
}
